import { useContext, useEffect, useRef, useState } from 'react';
import { createCardElement } from '@appcharge/ac-payment-element-js';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useTranslate } from '@/hooks/useTranslate';
import { logError } from '@/utils/logger';
import { getLogInfoData } from '@/utils/logs';
import { ErrorLogs } from '@/constants/logs';
import {
  APPCHARGE_PAYMENT_ELEMENT_FIELD_STYLES,
  APPCHARGE_PAYMENT_ELEMENT_FIELD_URLS,
} from '@/constants/appchargePaymentElement';
import {
  createInitialFieldMetadata,
  getAppchargePaymentElementFieldErrorMessage,
  type FieldMetadata,
} from '@/utils/appchargePaymentElement';
import type {
  AppchargePaymentElementCard,
  AppchargePaymentElementEntryMethod,
  AppchargePaymentElementFieldEvent,
  AppchargePaymentElementFieldId,
} from '@/types/appchargePaymentElement';
import { trackCardEntryStarted } from '@/utils/checkoutMixpanelTracking';
import { sendCardEntryStarted } from '@/utils/checkoutUiInteractionsTracking';
import { useLatestRef } from '@/hooks/useLatestRef';

/** Field bundles predating entry-method detection omit `entryMethod` on `change`. */
const resolveFieldEntryMethod = (
  card: AppchargePaymentElementCard | null,
  emittedBy: AppchargePaymentElementFieldId,
  state: AppchargePaymentElementFieldEvent['state']
): AppchargePaymentElementEntryMethod => state.entryMethod ?? card?.getFieldEntryMethod(emittedBy) ?? 'unknown';

export const useAppchargeCard = () => {
  const context = useContext(CheckoutContext);
  const {
    actions: { setError: setCheckoutError },
    card: { setCardElementRef },
  } = context;
  const { t } = useTranslate();

  const [isLoading, setIsLoading] = useState(true);
  const [cardElement, setCardElement] = useState<AppchargePaymentElementCard | null>(null);

  const numberFieldRef = useRef<HTMLDivElement>(null);
  const expiryFieldRef = useRef<HTMLDivElement>(null);
  const cvvFieldRef = useRef<HTMLDivElement>(null);
  const [cardNumberMetadata, setCardNumberMetadata] = useState<FieldMetadata>(() =>
    createInitialFieldMetadata(numberFieldRef)
  );
  const [expiryMetadata, setExpiryMetadata] = useState<FieldMetadata>(() => createInitialFieldMetadata(expiryFieldRef));
  const [cvvMetadata, setCvvMetadata] = useState<FieldMetadata>(() => createInitialFieldMetadata(cvvFieldRef));
  const isInitialized = useRef(false);
  const cardElementRef = useRef<AppchargePaymentElementCard | null>(null);
  const contextRef = useLatestRef(context);

  useEffect(() => {
    const numberEl = numberFieldRef.current;
    const expiryEl = expiryFieldRef.current;
    const cvvEl = cvvFieldRef.current;

    if (!numberEl || !expiryEl || !cvvEl) return;
    if (isInitialized.current) return;
    isInitialized.current = true;

    let card: AppchargePaymentElementCard | null = null;
    try {
      card = createCardElement({
        type: 'card',
        fields: { number: numberEl, expiry: expiryEl, cvv: cvvEl },
        fieldUrls: APPCHARGE_PAYMENT_ELEMENT_FIELD_URLS,
        styles: APPCHARGE_PAYMENT_ELEMENT_FIELD_STYLES,
      });
      setCardElement(card);
      cardElementRef.current = card;
      setCardElementRef(cardElementRef);

      card.ready
        .then(() => setIsLoading(false))
        .catch(() => {
          logError(ErrorLogs.appchargePaymentElementCardFormError, getLogInfoData(contextRef.current));
          setCheckoutError(t('paymentMethodNotAvailable'));
          setIsLoading(false);
        });
    } catch {
      logError(ErrorLogs.appchargePaymentElementCardFormError, getLogInfoData(contextRef.current));
      setCheckoutError(t('paymentMethodNotAvailable'));
      setIsLoading(false);
      isInitialized.current = false;
    }

    return () => {
      card?.destroy();
      // setCardElement(null);
      isInitialized.current = false;
    };
  }, [setCardElementRef]);

  const updateField = (field: AppchargePaymentElementFieldId, patch: Partial<Omit<FieldMetadata, 'ref'>>) => {
    switch (field) {
      case 'number':
        setCardNumberMetadata((prev) => ({ ...prev, ...patch }));
        break;
      case 'expiry':
        setExpiryMetadata((prev) => ({ ...prev, ...patch }));
        break;
      case 'cvv':
        setCvvMetadata((prev) => ({ ...prev, ...patch }));
        break;
    }
  };

  const handleValidityChange = ({ emittedBy, state }: AppchargePaymentElementFieldEvent) => {
    updateField(emittedBy, { isValid: state.isValid });
  };

  const handleBlur = ({ emittedBy, state }: AppchargePaymentElementFieldEvent) => {
    updateField(emittedBy, {
      isFocused: false,
      error: getAppchargePaymentElementFieldErrorMessage(emittedBy, state, t),
    });
  };

  const handleFocus = ({ emittedBy }: AppchargePaymentElementFieldEvent) => {
    updateField(emittedBy, { isFocused: true });
  };

  // Card entry is tracked from `change` rather than `focus`: the element SDK
  // classifies `entry_method` inside the field iframe and only once the shopper
  // has actually produced input, so a focus-time send would pin the event to
  // `'unknown'` for every session.
  const handleChange = ({ emittedBy, state }: AppchargePaymentElementFieldEvent) => {
    trackCardEntryStarted(contextRef.current);
    sendCardEntryStarted(
      contextRef.current,
      emittedBy,
      resolveFieldEntryMethod(cardElementRef.current, emittedBy, state)
    );
    updateField(emittedBy, { error: '' });
  };

  useEffect(() => {
    if (cardElement) {
      cardElement.on('validityChange', handleValidityChange);
      cardElement.on('blur', handleBlur);
      cardElement.on('focus', handleFocus);
      cardElement.on('change', handleChange);
    }
  }, [cardElement]);

  return {
    isCardFormValid: cardNumberMetadata.isValid && expiryMetadata.isValid && cvvMetadata.isValid,
    isLoading,
    cardNumberMetadata,
    expiryMetadata,
    cvvMetadata,
    numberFieldRef,
    expiryFieldRef,
    cvvFieldRef,
  };
};
